import React, { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { useMobileMenu } from "../../context/MobileMenuContext";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";
import {
  Home,
  Diamond,
  ClipboardList,
  Bell,
  Building2,
  Briefcase,
  Newspaper,
  Archive,
  Settings,
  HelpCircle,
  LucideIcon,
  ChevronDown,
  ChevronRight,
  FileSearch,
  FileEdit,
  Play,
  Star,
  Handshake,
  UserPlus,
  FileSignature,
  Send,
  Receipt,
  FileText,
} from "lucide-react";
import { Sheet, SheetContent } from "../ui/sheet";

interface SubNavItem {
  labelKey: string;
  href: string;
  icon?: LucideIcon;
}

interface NavItem {
  icon: LucideIcon;
  labelKey: string;
  href: string;
  entityTypes?: ("building" | "service_provider")[];
  subItemsProvider?: SubNavItem[];
  subItemsBuilding?: SubNavItem[];
}

// Submenu items for service providers
const providerOrderSubItems: SubNavItem[] = [
  { labelKey: "nav.orders.available", href: "/services?tab=available", icon: FileSearch },
  { labelKey: "nav.orders.myProposals", href: "/services?tab=applied", icon: FileEdit },
  { labelKey: "nav.orders.active", href: "/services?tab=active", icon: Play },
  { labelKey: "nav.orders.favorites", href: "/services?tab=favorites", icon: Star },
];

// Submenu items for buildings
const buildingOrderSubItems: SubNavItem[] = [
  { labelKey: "nav.orders.newOrder", href: "/services?tab=new", icon: FileEdit },
  { labelKey: "nav.orders.pending", href: "/services?tab=pending", icon: FileSearch },
  { labelKey: "nav.orders.active", href: "/services?tab=active", icon: Play },
  { labelKey: "nav.orders.favorites", href: "/services?tab=favorites", icon: Star },
];

// Partners submenu mirrors the page's tabs, so it reuses their i18n keys.
// The tab set is shared by both entity types; buildings additionally get the
// add-partner entry (ticket 3.7), the way orders have «Новый заказ».
const partnerSubItems: SubNavItem[] = [
  { labelKey: "partners.tabs.active", href: "/partners", icon: Handshake },
  { labelKey: "partners.tabs.pending", href: "/partners?tab=pending", icon: FileSignature },
  { labelKey: "partners.tabs.archived", href: "/partners?tab=archived", icon: Archive },
  { labelKey: "partners.tabs.requests", href: "/partners?tab=requests", icon: Send },
];
const partnerSubItemsBuilding: SubNavItem[] = [
  { labelKey: "partners.addPartner.modeAdd", href: "/partners/new", icon: UserPlus },
  ...partnerSubItems,
];

// The tab a section shows when the URL carries no ?tab= — used to mark the
// matching sub-item active on the bare URL.
const DEFAULT_SECTION_TAB: Record<string, Partial<Record<"building" | "service_provider", string>>> = {
  "/services": { building: "new", service_provider: "available" },
  "/partners": { building: "active", service_provider: "active" },
};

const baseNavItems: NavItem[] = [
  { icon: Home, labelKey: "nav.home", href: "/dashboard" },
  {
    icon: Diamond,
    labelKey: "nav.partners",
    href: "/partners",
    subItemsProvider: partnerSubItems,
    subItemsBuilding: partnerSubItemsBuilding,
  },
  { 
    icon: ClipboardList, 
    labelKey: "nav.orders", 
    href: "/services",
    subItemsProvider: providerOrderSubItems,
    subItemsBuilding: buildingOrderSubItems,
  },
  { icon: Bell, labelKey: "nav.notifications", href: "/notifications" },
  // Ticket 25: «Дополнительные услуги», a group like «Заказы». Provider-only,
  // and deliberately no `subItemsBuilding` — a building must not see it even
  // if `entityTypes` is ever loosened.
  {
    icon: Receipt,
    labelKey: "nav.extraServices",
    href: "/extra-services",
    entityTypes: ["service_provider"],
    subItemsProvider: [{ labelKey: "nav.invoicing", href: "/extra-services/invoices", icon: FileText }],
  },
  // Entity-specific items
  { icon: Building2, labelKey: "nav.houseInfo", href: "/house-info", entityTypes: ["building"] },
  { icon: Briefcase, labelKey: "nav.companyInfo", href: "/company-info", entityTypes: ["service_provider"] },
  // Common items
  { icon: Newspaper, labelKey: "nav.news", href: "/news" },
  { icon: Archive, labelKey: "nav.archives", href: "/archive" },
  { icon: Settings, labelKey: "nav.settings", href: "/settings" },
  { icon: HelpCircle, labelKey: "nav.help", href: "/help" },
];

interface SidebarContentProps {
  onNavClick?: () => void;
}

function SidebarContent({ onNavClick }: SidebarContentProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { entityType } = useAuth();
  const { interceptNavigation } = useUnsavedChanges();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Get subItems for current entity type
  const getSubItems = (item: NavItem): SubNavItem[] | undefined => {
    if (entityType === "service_provider") return item.subItemsProvider;
    if (entityType === "building") return item.subItemsBuilding;
    return undefined;
  };

  // A parent with submenus is active anywhere under its path (e.g. /services/order/:id)
  const isItemSectionActive = (item: NavItem) =>
    location.pathname === item.href || location.pathname.startsWith(item.href + "/");

  // A sub-item is active when its tab param matches the current one; the tab
  // shown by default (no ?tab= in the URL) also counts as active. A sub-item
  // with its own path (e.g. /partners/new) matches that path exactly, and the
  // tab items stay dark there.
  const isSubItemActive = (item: NavItem, subItem: SubNavItem) => {
    const [subPath, subQuery] = subItem.href.split("?");
    if (subPath !== item.href) {
      return !subQuery && (location.pathname === subPath || location.pathname.startsWith(subPath + "/"));
    }
    if (location.pathname !== item.href) return false;
    const currentTab = new URLSearchParams(location.search).get("tab");
    const subTab = new URLSearchParams(subItem.href.split("?")[1] ?? "").get("tab");
    const defaultTab = entityType ? DEFAULT_SECTION_TAB[item.href]?.[entityType] ?? null : null;
    return (currentTab ?? defaultTab) === (subTab ?? defaultTab);
  };

  // Auto-expand submenu if current location is inside its section
  React.useEffect(() => {
    baseNavItems.forEach((item) => {
      const subItems = getSubItems(item);
      if (subItems && isItemSectionActive(item) && !expandedItems.includes(item.href)) {
        setExpandedItems((prev) => [...prev, item.href]);
      }
    });
  }, [location, entityType]);

  // Filter nav items based on entity type
  const navItems = useMemo(() => {
    return baseNavItems.filter(item => {
      // If item has no entityTypes restriction, show for everyone
      if (!item.entityTypes) return true;
      // If entityType is null (user auth), hide entity-specific items
      if (!entityType) return false;
      // Show only if current entityType is in allowed list
      return item.entityTypes.includes(entityType);
    });
  }, [entityType]);

  const toggleExpand = (href: string) => {
    setExpandedItems((prev) =>
      prev.includes(href)
        ? prev.filter((h) => h !== href)
        : [...prev, href]
    );
  };

  // Let the unsaved-changes guard veto the navigation (it shows its own
  // confirmation dialog and re-navigates if the user confirms leaving)
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    if (!interceptNavigation(href, onNavClick)) {
      e.preventDefault();
      return;
    }
    onNavClick?.();
  };

  return (
    <>
      <nav className="flex flex-col gap-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.href && !location.search;
          const subItems = getSubItems(item);
          const hasSubItems = !!subItems && subItems.length > 0;
          const isExpanded = expandedItems.includes(item.href);

          // For items with submenus
          if (hasSubItems && subItems) {
            const isAnySubActive = isItemSectionActive(item);
            // On sub-pages (e.g. /services/order/:id) no sub-item matches the
            // location, so the parent carries the full highlight to keep the
            // current section visible in the menu. When a sub-item IS active
            // (a tab, or a path-based entry like /partners/new) the parent only
            // gets the light accent — the sub-item is the highlighted one.
            const isOnSubPage =
              isAnySubActive && !subItems.some((sub) => isSubItemActive(item, sub));

            return (
              <div key={item.href}>
                <button
                  onClick={() => toggleExpand(item.href)}
                  className={cn(
                    "tap-target w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-left text-sm font-medium transition-all duration-200",
                    isOnSubPage
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : isAnySubActive
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    {t(item.labelKey)}
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {/* A real 44 px height here rather than `.tap-target`'s overlay:
                        these sit 2 px apart, so an overlay would reach 6 px into
                        the neighbour and a near miss would open the wrong tab
                        (BUG-63, caught in code review). */}
                    {subItems.map((subItem) => {
                      const isSubActive = isSubItemActive(item, subItem);
                      const SubIcon = subItem.icon;
                      return (
                        <NavLink
                          key={subItem.href}
                          to={subItem.href}
                          onClick={(e) => handleNavClick(e, subItem.href)}
                          className={cn(
                            "flex min-h-[44px] items-center gap-2.5 px-4 py-2 rounded-lg text-sm transition-all duration-200",
                            isSubActive
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                          )}
                        >
                          {SubIcon && <SubIcon className="w-4 h-4" />}
                          {t(subItem.labelKey)}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Regular nav items
          return (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className={cn(
                "tap-target flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
              )}>
              <item.icon
                className={cn(
                  "w-5 h-5 transition-transform duration-200",
                  isActive ? "" : "group-hover:scale-105"
                )}
              />
              {t(item.labelKey)}
            </NavLink>
          );
        })}
      </nav>

    </>
  );
}

// Desktop sidebar
export function Sidebar() {
  return (
    <aside className="hidden lg:flex w-[278px] bg-sidebar backdrop-blur-xl flex-col h-[calc(100vh-4rem)] sticky top-16 px-4 py-8 gap-8 border-r border-sidebar-border overflow-y-auto">
      <SidebarContent />
    </aside>
  );
}

// Mobile sidebar (sheet)
export function MobileSidebar() {
  const { isOpen, close } = useMobileMenu();

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && close()}>
      <SheetContent side="left" className="w-[280px] p-0" showCloseButton={false}>
        <div className="flex flex-col h-full px-4 py-8 gap-8">
          {/* Logo */}
          <div className="px-4 flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="Unikort" 
              className="h-9 w-auto"
            />
            <span className="text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">
              Unikort
            </span>
          </div>
          {/*
            The entries scroll inside the panel. Below ~492 px of viewport
            height — 200 % browser zoom on an ordinary laptop — the nine of
            them do not fit, and without this the last three are cut off by
            the bottom of the screen with no way to reach them: the panel is
            `fixed`, so the wheel moves the page behind it and the entries
            stay where they are (BUG-65). `min-h-0` is what makes it scroll —
            a flex item will not shrink below its content height without it.

            `pt-0.5` is for the first entry: its `.tap-target` area reaches
            2 px above it (40 tall, 44 area), and a scroll container clips
            whatever sticks out above its top, so «Главная» was 42 tall to a
            finger (BUG-63 re-test). The bottom needs nothing — overflow below
            becomes scrollable instead of being cut off.
          */}
          <div className="flex-1 min-h-0 overflow-y-auto pt-0.5">
            <SidebarContent onNavClick={close} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
